<template>
  <div class="batch-samples">
    <md-button
      class="btn btn-nonenary"
      :disabled="isDisabled"
      @click="handleBatchSelectedSamples"
    >
      <md-icon md-src="/static/images/icon-batch-sample.svg" />

      Lotear amostras
    </md-button>

    <modal
      ref="batchesModal"
      template="neutral"
      ref-name="batchesModal"
      :title="title"
      :icon-enabled="false"
      :close-button-enabled="true"
      @close="resetModalParams"
    >
      <batch-samples-sample-details
        v-if="step === ModalToBatchSamplesStep.SampleDetails && detailedSample"
        :sample="detailedSample"
        @continue="handleBatchDetailedSample"
      />

      <batch-samples-batch-selection
        v-else-if="step === ModalToBatchSamplesStep.BatchSelection"
        :sample="detailedSample"
        @continue="handleConfirmBatch($event)"
        @samplesBatched="handleSamplesBatched"
        @update:title="handleUpdateTitle"
      />

      <batch-samples-batch-confirmation
        v-else-if="step === ModalToBatchSamplesStep.BatchConfirmation"
        :sample="detailedSample"
        :batch="selectedBatch"
        @cancel="handleClose"
        @close="handleClose"
        @samplesBatched="handleSamplesBatched"
        @openBatch="handleOpenBatch"
        @update:title="handleUpdateTitle"
      />
    </modal>

    <incompatible-samples-modal ref="incompatibleSamplesModal" />

    <no-paternity-samples-modal ref="noPaternitySamplesModal" />

    <mixed-origin-samples-modal ref="mixedOriginSamplesModal" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { ModalToBatchSamplesStep } from '@/enums'
import {
  compareDestinationByMnemonic,
  comparePackagingByMnemonic,
  isSamplePaternity,
  isSampleUrgent,
} from '@/utils/sample-utils'
import { isWsSampleOrigin } from '@/components/Batch/batchSource'

import notify from '@/api/notification'
import { MAX_SAMPLES_PER_BATCH } from '@/constants/batch'

import BatchSamplesSampleDetails from './BatchSamplesSampleDetails'
import BatchSamplesBatchSelection from './BatchSamplesBatchSelection'
import BatchSamplesBatchConfirmation from './BatchSamplesBatchConfirmation'
import IncompatibleSamplesModal from './IncompatibleSamplesModal'
import NoPaternitySamplesModal from './NoPaternitySamplesModal'
import MixedOriginSamplesModal from './MixedOriginSamplesModal'

export default {
  name: 'BatchSamples',
  components: {
    BatchSamplesSampleDetails,
    BatchSamplesBatchSelection,
    BatchSamplesBatchConfirmation,
    IncompatibleSamplesModal,
    NoPaternitySamplesModal,
    MixedOriginSamplesModal,
  },
  data() {
    return {
      ModalToBatchSamplesStep,
      title: null,
      step: null,
      detailedSample: null,
      selectedBatch: null,
      samplesListUpdatePending: false,
    }
  },
  computed: {
    ...mapGetters(['selectedSamples']),
    isDisabled() {
      return this.selectedSamples.length === 0
    },
  },
  methods: {
    resetModalParams() {
      this.title = null
      this.step = null
      this.detailedSample = null
      this.selectedBatch = null

      if (this.samplesListUpdatePending) {
        this.samplesListUpdatePending = false
        this.$emit('requestSamplesListUpdate')
      }
    },
    containsDivergentSamples(selectedSamples) {
      const [firstSelectedSample] = selectedSamples

      return selectedSamples.some(
        sample =>
          sample.packagingId !== firstSelectedSample.packagingId ||
          !compareDestinationByMnemonic(firstSelectedSample, sample) ||
          !comparePackagingByMnemonic(firstSelectedSample, sample) ||
          isSampleUrgent(sample) !== isSampleUrgent(firstSelectedSample)
      )
    },
    containsPaternitySamples(selectedSamples) {
      return selectedSamples.some(sample => isSamplePaternity(sample))
    },
    containsMixedOriginSamples(selectedSamples) {
      if (selectedSamples.length < 2) {
        return false
      }

      const hasWsSample = selectedSamples.some(sample => isWsSampleOrigin(sample.origem))
      const hasNonWsSample = selectedSamples.some(sample => !isWsSampleOrigin(sample.origem))

      return hasWsSample && hasNonWsSample
    },
    detailSample(sample) {
      this.title = 'Detalhes da amostra'
      this.detailedSample = sample
      this.step = ModalToBatchSamplesStep.SampleDetails

      this.$refs.batchesModal.open()
    },
    handleBatchSelectedSamples() {
      if (this.selectedSamples.length > MAX_SAMPLES_PER_BATCH) {
        notify({
          title: 'Limite excedido',
          message: `Selecione no máximo ${MAX_SAMPLES_PER_BATCH} amostras por lote.`,
        })
        return
      }

      if (this.containsMixedOriginSamples(this.selectedSamples)) {
        this.$refs.mixedOriginSamplesModal.open()
      } else if (this.containsDivergentSamples(this.selectedSamples)) {
        this.$refs.incompatibleSamplesModal.open()
      } else if (this.containsPaternitySamples(this.selectedSamples)) {
        this.$refs.noPaternitySamplesModal.open()
      } else {
        this.title = null
        this.step = ModalToBatchSamplesStep.BatchSelection

        this.$refs.batchesModal.open()
      }
    },
    handleBatchDetailedSample() {
      this.title = null
      this.step = ModalToBatchSamplesStep.BatchSelection
    },
    handleConfirmBatch(batch) {
      this.title = null
      this.selectedBatch = batch
      this.step = ModalToBatchSamplesStep.BatchConfirmation
    },
    async handleClose() {
      await this.$refs.batchesModal.close()

      this.title = null
      this.detailedSample = null
      this.step = ModalToBatchSamplesStep.SampleDetails
    },
    handleSamplesBatched() {
      this.samplesListUpdatePending = true
    },
    handleOpenBatch(batchIdentifier) {
      if (!batchIdentifier) return
      this.$router.push(`/print-tag/batches/${batchIdentifier}`)
    },
    handleUpdateTitle(newTitle) {
      this.title = newTitle
    }
  }
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;

:deep(.standard-modal__title) {
  color: $theme-primary-color;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  padding-top: 0.25rem;
}

:deep(.md-dialog) {
  min-width: auto !important;
  width: fit-content !important;
  max-width: none !important;
}

.md-button {
  height: 40px;
}
</style>
